import React from 'react';
import { motion } from 'framer-motion';
import { Flag, MapPin, Navigation } from 'lucide-react';
import { SlideDef, SLIDES, nameOf } from './slides';
import { Editable } from './Editable';
import { SourceLine, TrustBadge } from './SourceLine';
import { ImageSlot } from './ImageSlot';
import { MapBg } from './Chrome';
import { KraftBg, Paperclip, Stamp } from './Dossier';
import { useCanEdit, useDeck } from './store';
import { collectSources } from './sources';

const rise = (d = 0) => ({ initial: { y: 24, opacity: 0 }, animate: { y: 0, opacity: 1 }, transition: { duration: 0.45, delay: d, ease: 'easeOut' } });

function Kicker({ n, children, dark = true }: { n: string; children: React.ReactNode; dark?: boolean }) {
  return (
    <motion.div {...rise()} className={`flex items-center gap-3 font-type text-[18px] uppercase tracking-[0.2em] ${dark ? 'text-white/60' : 'text-black/55'}`}>
      <span className="h-2.5 w-2.5 rounded-full bg-blue-600" /><span className="h-2.5 w-2.5 bg-red-600" />
      <span>{n}</span><span className={dark ? 'text-white/30' : 'text-black/30'}>—</span><span>{children}</span>
    </motion.div>
  );
}

function Names({ dark = true }: { dark?: boolean }) {
  const { team } = useDeck();
  return (
    <div className="flex flex-wrap gap-3" data-testid="cover-names">
      {[0, 1, 2].map((i) => (
        <span key={i} className={`rounded-full border px-4 py-1.5 font-app text-[20px] font-medium ${dark ? 'border-white/25 text-white' : 'border-black/25 text-black'}`}>{nameOf(i, team)}</span>
      ))}
    </div>
  );
}

export function Cover({ d }: { d: SlideDef }) {
  return (
    <div className="absolute inset-0 bg-[#0a0a0a]">
      <div className="absolute inset-0" style={{ clipPath: 'polygon(0 0, 62% 0, 48% 100%, 0 100%)' }}><MapBg dark /></div>
      <div className="absolute inset-0" style={{ clipPath: 'polygon(62% 0, 100% 0, 100% 100%, 48% 100%)' }}>
        <KraftBg />
        <Stamp className="absolute right-[90px] top-[110px] text-[30px]" rotate={-9} testId="cover-stamp">Dossier n° L1-EG</Stamp>
      </div>
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent" style={{ width: '62%' }} />
      <div className="absolute left-[90px] top-[150px] flex w-[880px] flex-col gap-6">
        <Kicker n="Exposé">Plateformes numériques · L1 Éco-Gestion</Kicker>
        <motion.div {...rise(0.1)}>
          <Editable k={`${d.id}:title`} ph="Titre de la présentation" max={90} size={84} boxClass="h-[270px]" className="font-app font-bold leading-[1.02] tracking-tight text-white" testId="cover-title" metaDark />
        </motion.div>
        <motion.div {...rise(0.18)}>
          <Editable k={`${d.id}:subtitle`} ph="Sous-titre (facultatif)" max={110} size={28} boxClass="h-[80px]" className="font-type text-white/75" testId="cover-subtitle" metaDark />
        </motion.div>
        <motion.div {...rise(0.25)} className="flex flex-col gap-4">
          <Names />
          <Editable k={`${d.id}:date`} ph="Date de l'oral" max={40} size={22} boxClass="h-[36px] w-[420px]" className="font-type text-white/60" testId="cover-date" metaDark />
        </motion.div>
      </div>
      <div className="absolute bottom-[90px] right-[110px] h-[330px] w-[400px] rotate-[2deg]">
        <Paperclip className="absolute -top-10 left-8 z-10" />
        <ImageSlot sid={d.id} className="h-full w-full" frame="bg-white p-3 shadow-2xl" />
      </div>
    </div>
  );
}

export function Intro({ d }: { d: SlideDef }) {
  return (
    <div className="absolute inset-0 bg-[#f6f6f3] text-black">
      <div className="absolute right-0 top-0 h-full w-[8px] bg-blue-600" /><div className="absolute right-[8px] top-0 h-full w-[8px] bg-red-600" />
      <div className="absolute left-[100px] top-[90px] flex h-[730px] w-[880px] flex-col gap-6">
        <Kicker n="01" dark={false}>Introduction</Kicker>
        <motion.div {...rise(0.08)}><Editable k={`${d.id}:title`} ph="Présentation du thème" max={80} size={58} boxClass="h-[150px]" className="font-app font-bold leading-[1.05] tracking-tight" testId="intro-title" /></motion.div>
        <motion.div {...rise(0.16)} className="h-[3px] w-24 bg-black" />
        <motion.div {...rise(0.22)} className="flex min-h-0 flex-1 flex-col gap-4">
          <Editable k={`${d.id}:body`} ph="Contexte, définitions, chiffres clés…" max={600} size={28} boxClass="flex-1" className="font-ui leading-relaxed text-neutral-800" testId="intro-body" />
          <SourceLine k={`${d.id}:body:src`} parentKey={`${d.id}:body`} testId="intro-body-src" />
        </motion.div>
      </div>
      <motion.div {...rise(0.3)} className="absolute right-[100px] top-[120px] h-[660px] w-[440px]">
        <ImageSlot sid={d.id} className="h-full w-full" frame="rounded-2xl bg-white p-3 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.4)]" />
      </motion.div>
    </div>
  );
}

export function Plan({ d }: { d: SlideDef }) {
  const { get } = useDeck();
  const stops = [
    { code: 'Départ', label: 'Introduction', x: 7, axis: 0 },
    ...SLIDES.filter((s) => s.part).map((s, i) => ({ code: s.part!, label: (get(`${s.id}:title`) as string) || '', x: 18 + i * 11.5 + (i > 2 ? 3 : 0), axis: i < 3 ? 1 : 2 })),
    { code: 'Arrivée', label: 'Verdict', x: 91, axis: 3 },
  ];
  return (
    <div className="absolute inset-0 bg-[#0a0a0a] text-white">
      <div className="absolute left-[100px] top-[80px] flex w-[1220px] flex-col gap-5">
        <Kicker n="02">Problématique</Kicker>
        <motion.div {...rise(0.08)} className="relative pl-10">
          <span className="absolute left-0 top-[-14px] font-elite text-[90px] leading-none text-red-600">«</span>
          <Editable k={`${d.id}:problem`} ph="Problématique" max={220} size={46} boxClass="h-[210px]" className="font-app font-semibold leading-[1.15] tracking-tight" testId="plan-problem" metaDark />
        </motion.div>
        <SourceLine k={`${d.id}:problem:src`} parentKey={`${d.id}:problem`} testId="plan-problem-src" dark />
      </div>
      <div className="absolute right-[90px] top-[90px] h-[180px] w-[240px]"><ImageSlot sid={d.id} className="h-full w-full" dark /></div>
      <div className="absolute inset-x-0 top-[470px] h-[360px]" data-testid="plan-itinerary">
        <div className="absolute left-[100px] top-0 font-type text-[16px] uppercase tracking-[0.25em] text-white/50">Itinéraire</div>
        <div className="absolute top-[40px] h-[60px] rounded-xl border border-blue-600/50 bg-blue-600/10 px-4 py-2" style={{ left: '15%', width: '29%' }}>
          <span className="font-app text-[15px] font-bold text-blue-400">I.</span> <span className="font-app text-[17px] text-white/90">{get('s4:title') || <span className="text-white/30">Titre de l'axe 1</span>}</span>
        </div>
        <div className="absolute top-[40px] h-[60px] rounded-xl border border-red-600/50 bg-red-600/10 px-4 py-2" style={{ left: '52%', width: '30%' }}>
          <span className="font-elite text-[15px] text-red-400">II.</span> <span className="font-type text-[17px] text-white/90">{get('s8:title') || <span className="text-white/30">Titre de l'axe 2</span>}</span>
        </div>
        <svg className="absolute inset-x-0 top-[150px] h-[20px] w-full" viewBox="0 0 1600 20" preserveAspectRatio="none">
          <motion.line x1="112" y1="10" x2="656" y2="10" stroke="#2563eb" strokeWidth="6" strokeLinecap="round" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.6 }} />
          <line x1="656" y1="10" x2="888" y2="10" stroke="#666" strokeWidth="4" strokeDasharray="6 8" />
          <motion.line x1="888" y1="10" x2="1456" y2="10" stroke="#dc2626" strokeWidth="6" strokeDasharray="14 10" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.6, delay: 0.4 }} />
        </svg>
        {stops.map((s, i) => (
          <motion.div key={s.code} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.2 + i * 0.07 }} className="absolute top-[138px] -ml-[85px] w-[170px]" style={{ left: `${s.x}%` }}>
            <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full border-4 border-[#0a0a0a] bg-white text-black">
              {i === 0 ? <Navigation size={18} /> : i === stops.length - 1 ? <Flag size={18} className="text-red-600" /> : <MapPin size={18} className={s.axis === 1 ? 'text-blue-600' : 'text-red-600'} />}
            </div>
            <div className="mt-3 text-center">
              <div className={`font-app text-[20px] font-bold ${s.axis === 1 ? 'text-blue-400' : s.axis === 2 ? 'text-red-400' : 'text-white'}`}>{s.code}</div>
              <div className="mt-1 line-clamp-3 font-ui text-[15px] leading-snug text-white/80">{s.label || <span className="text-white/25">titre à venir</span>}</div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

export function Verdict({ d }: { d: SlideDef }) {
  return (
    <div className="absolute inset-0 bg-[#f4f3ef] text-black">
      <div className="absolute bottom-[70px] left-[100px] right-[100px] h-[3px] bg-black/10" />
      <motion.div className="absolute bottom-[70px] left-[100px] h-[3px] bg-blue-600" initial={{ width: 0 }} animate={{ width: 1400 }} transition={{ duration: 0.8, ease: 'easeOut' }} />
      <Flag className="absolute bottom-[60px] right-[86px] text-blue-600" size={28} />
      <div className="absolute left-[100px] top-[80px] flex h-[690px] w-[960px] flex-col gap-5">
        <Kicker n="03" dark={false}>Conclusion</Kicker>
        <motion.h2 {...rise(0.05)} className="font-elite text-[110px] leading-none tracking-tight">Verdict</motion.h2>
        <motion.div {...rise(0.15)} className="flex min-h-0 flex-1 flex-col gap-3 border-l-4 border-black pl-6">
          <span className="font-type text-[16px] uppercase tracking-[0.2em] text-black/50">Réponse à la problématique</span>
          <Editable k={`${d.id}:answer`} ph="Réponse à la problématique" max={480} size={30} boxClass="flex-1" className="font-app font-medium leading-snug" testId="verdict-answer" />
          <SourceLine k={`${d.id}:answer:src`} parentKey={`${d.id}:answer`} testId="verdict-answer-src" />
        </motion.div>
        <motion.div {...rise(0.22)} className="flex h-[150px] flex-col gap-2 pl-7">
          <span className="font-type text-[16px] uppercase tracking-[0.2em] text-black/50">Ouverture</span>
          <Editable k={`${d.id}:open`} ph="Ouverture" max={220} size={24} boxClass="flex-1" className="font-type leading-snug text-neutral-800" testId="verdict-open" />
        </motion.div>
      </div>
      <Stamp className="absolute right-[150px] top-[110px] text-[34px]" rotate={-14} delay={0.45} testId="verdict-stamp">Affaire jugée</Stamp>
      <div className="absolute right-[100px] top-[260px] h-[420px] w-[420px]"><ImageSlot sid={d.id} className="h-full w-full" frame="rounded-xl bg-white p-3 shadow-xl" /></div>
    </div>
  );
}

export function Sources({ d }: { d: SlideDef }) {
  const { data } = useDeck();
  const canEdit = useCanEdit();
  const list = collectSources(data);
  const fs = list.length > 18 ? 13 : list.length > 10 ? 15 : 18;
  return (
    <div className="absolute inset-0 bg-[#0a0a0a] text-white">
      <div className="absolute left-[100px] top-[70px] flex h-[780px] w-[1400px] flex-col gap-5">
        <Kicker n="Annexe">Bibliographie · norme ISO 690</Kicker>
        <h2 className="font-app text-[64px] font-bold tracking-tight">Sources</h2>
        <div className="min-h-0 flex-1 overflow-hidden columns-2 gap-12" data-testid="sources-list">
          {list.length === 0 && <p className="font-type text-[18px] text-white/35">Les sources indiquées à côté de chaque extrait apparaîtront ici automatiquement.</p>}
          {list.map((s, i) => (
            <div key={s.k} className="mb-3 break-inside-avoid font-type leading-snug text-white/85" style={{ fontSize: fs }} data-testid={`sources-item-${i}`}>
              <span className="mr-2 text-blue-400">[{i + 1}]</span>{s.text}
              {canEdit && <span className="ml-2 align-middle"><TrustBadge src={s.text} /></span>}
            </div>
          ))}
        </div>
        <Editable k={`${d.id}:extra`} ph="Autres références (ouvrages, cours, articles consultés…)" max={500} size={16} boxClass="h-[90px]" className="font-type text-white/70" testId="sources-extra" metaDark />
      </div>
    </div>
  );
}

export function Merci() {
  const { team } = useDeck();
  return (
    <div className="absolute inset-0 bg-[#0a0a0a]">
      <div className="absolute inset-y-0 right-0 w-[46%] bg-[#f4f3ef]" />
      <motion.h2 {...rise()} className="absolute left-[100px] top-[230px] font-app text-[220px] font-bold leading-none tracking-tighter text-white">Merci.</motion.h2>
      <div className="absolute left-[106px] top-[500px] flex gap-3 font-type text-[22px] text-white/60">{[0, 1, 2].map((i) => <span key={i}>{nameOf(i, team)}{i < 2 ? ' ·' : ''}</span>)}</div>
      <motion.div {...rise(0.15)} className="absolute right-[70px] top-[300px] text-right">
        <div className="font-elite text-[92px] leading-none text-black">Questions ?</div>
        <div className="mt-6 flex items-center justify-end gap-3 font-ui text-[20px] text-black/60"><MapPin className="text-blue-600" /> Fin de course</div>
      </motion.div>
      <Stamp className="absolute bottom-[130px] right-[160px] text-[28px]" rotate={-8} delay={0.4}>Fin de l'audience</Stamp>
    </div>
  );
}
